import {
  useWSSnackbar,
  WSButton,
  WSCard,
  WSElement,
  WSFlexBox,
  WSLoader,
  WSSectionToolbar,
  WSTable,
  WSTableCell,
  WSTableColumn,
  WSText
} from "@wingspanhq/fe-component-library";
import { WSFileAccessScope } from "@wingspanhq/files/dist/lib/interfaces";
import { IWorkLog, WorkLogStatus } from "@wingspanhq/payments/dist/interfaces";
import React, { useMemo } from "react";
import { useDownloadFile } from "../../../query/files/mutations";
import { useMemberPrivateFileQuery } from "../../../query/files/queries";
import { useMutationUpdateWorkLog } from "../../../query/workLog/mutations/useMutationUpdateWorkLog";
import { useModalEditWorkLogNotes } from "../modals/ModalEditWorkLogNotes/useModalEditWorkLogNotes";
import { useModalUploadWorkLogAttachment } from "../modals/ModalUploadWorkLogAttachment/useModalUploadWorkLogAttachment";
import styles from "./WorkLogAdditionalDetailsPayeeView.module.scss";

type Props = {
  workLog?: IWorkLog;
  shimmer?: boolean;
  payerId?: string;
  payeeId?: string;
};

type AttachmentData = {
  fileId: string;
  workLogId: string;
  existingAttachmentIds: string[];
  isEditable: boolean;
};

const AttachmentFilenameCell: React.FC<{ fileId: string }> = ({ fileId }) => {
  const queryFile = useMemberPrivateFileQuery(fileId);

  if (queryFile.isLoading) {
    return <WSLoader.Spinner size="XS" />;
  }

  const filename = queryFile.data?.filename || "Unknown file";
  return <WSTableCell text={filename} />;
};

const AttachmentActionsCell: React.FC<{
  fileId: string;
  workLogId: string;
  existingAttachmentIds: string[];
  isEditable: boolean;
}> = ({ fileId, workLogId, existingAttachmentIds, isEditable }) => {
  const { openSnackbar } = useWSSnackbar();
  const [downloadFile, downloadFileMeta] = useDownloadFile();
  const [updateWorkLog, updateWorkLogMeta] = useMutationUpdateWorkLog();

  const handleView = async () => {
    await downloadFile({
      accessScope: WSFileAccessScope.Private,
      fileId
    });
  };

  const handleRemove = async () => {
    const newAttachmentIds = existingAttachmentIds.filter(id => id !== fileId);
    await updateWorkLog(
      { workLogId, request: { attachmentIds: newAttachmentIds } },
      {
        onSuccess: () => {
          openSnackbar({
            icon: { name: "check" },
            message: "Attachment removed"
          });
        },
        onError: () => {
          openSnackbar({
            message: "Failed to remove attachment",
            type: "error"
          });
        }
      }
    );
  };

  return (
    <WSFlexBox.CenterY>
      <WSButton.Link
        size="S"
        onClick={handleView}
        loading={downloadFileMeta.isLoading}
      >
        View
      </WSButton.Link>
      {isEditable && (
        <>
          <WSText.ParagraphXs color="gray300" mx="XS">
            |
          </WSText.ParagraphXs>
          <WSButton.Link
            size="S"
            onClick={handleRemove}
            loading={updateWorkLogMeta.isLoading}
          >
            Remove
          </WSButton.Link>
        </>
      )}
    </WSFlexBox.CenterY>
  );
};

const getAttachmentsColumns = (): WSTableColumn<AttachmentData>[] => [
  {
    renderFunction: item => <AttachmentFilenameCell fileId={item.data.fileId} />
  },
  {
    config: {
      justify: "end"
    },
    renderFunction: item => (
      <AttachmentActionsCell
        fileId={item.data.fileId}
        workLogId={item.data.workLogId}
        existingAttachmentIds={item.data.existingAttachmentIds}
        isEditable={item.data.isEditable}
      />
    )
  }
];

export const WorkLogAdditionalDetailsPayeeView: React.FC<Props> = ({
  workLog,
  shimmer,
  payerId,
  payeeId
}) => {
  const modalEditNotes = useModalEditWorkLogNotes();
  const modalUploadAttachment = useModalUploadWorkLogAttachment();

  const isEditable =
    workLog?.status === WorkLogStatus.Draft && !workLog?.invoiceId;
  const attachmentIds = workLog?.attachmentIds || [];
  const hasNotes = !!workLog?.notes;
  const hasAttachments = attachmentIds.length > 0;

  const tableData = useMemo(() => {
    return attachmentIds.map(fileId => ({
      id: fileId,
      data: {
        fileId,
        workLogId: workLog?.workLogId || "",
        existingAttachmentIds: attachmentIds,
        isEditable
      }
    }));
  }, [attachmentIds, workLog?.workLogId, isEditable]);

  const columns = useMemo(() => getAttachmentsColumns(), []);

  if (shimmer) {
    return (
      <WSElement>
        <WSSectionToolbar title="Additional details" />
        <WSCard mt="M" shimmer />
      </WSElement>
    );
  }

  const handleAddNotes = () => {
    if (workLog) {
      modalEditNotes.open({
        workLogId: workLog.workLogId,
        currentNotes: workLog.notes
      });
    }
  };

  const handleAddAttachment = () => {
    if (workLog && payerId && payeeId) {
      modalUploadAttachment.open({
        workLogId: workLog.workLogId,
        payerId,
        payeeId,
        existingAttachmentIds: attachmentIds
      });
    }
  };

  // Empty state: show add buttons
  if (!hasNotes && !hasAttachments && isEditable && workLog) {
    return (
      <WSElement>
        <WSSectionToolbar title="Additional details" />
        <WSCard mt="M">
          <WSFlexBox direction="column" gap="L">
            <WSElement className={styles.addLineItem}>
              <WSButton.Link icon="edit" onClick={handleAddNotes}>
                Add notes
              </WSButton.Link>
            </WSElement>
            {payerId && payeeId && (
              <WSElement className={styles.addLineItem}>
                <WSButton.Link icon="paperclip" onClick={handleAddAttachment}>
                  Add attachment
                </WSButton.Link>
              </WSElement>
            )}
          </WSFlexBox>
        </WSCard>
      </WSElement>
    );
  }

  return (
    <WSElement>
      <WSSectionToolbar title="Additional details" />
      <WSCard mt="M">
        {/* Notes Section */}
        <WSElement>
          <WSFlexBox.CenterY justify="space-between" mb="XS">
            <WSText.ParagraphSm color="gray500">Notes</WSText.ParagraphSm>
            {isEditable && workLog && (
              <WSButton.Link size="S" onClick={handleAddNotes}>
                {hasNotes ? "Edit" : "Add notes"}
              </WSButton.Link>
            )}
          </WSFlexBox.CenterY>
          <WSText.ParagraphSm color={hasNotes ? "gray700" : "gray400"}>
            {workLog?.notes || "No notes"}
          </WSText.ParagraphSm>
        </WSElement>

        {/* Attachments Section */}
        <WSElement mt="XL">
          <WSFlexBox.CenterY justify="space-between" mb="S">
            <WSText.ParagraphSm color="gray500">Attachments</WSText.ParagraphSm>
            {isEditable && workLog && payerId && payeeId && hasAttachments && (
              <WSButton.Link size="S" onClick={handleAddAttachment}>
                Add attachment
              </WSButton.Link>
            )}
          </WSFlexBox.CenterY>

          {hasAttachments && workLog ? (
            <WSTable<AttachmentData>
              showHeader={false}
              tableData={tableData}
              columns={columns}
            />
          ) : isEditable && workLog && payerId && payeeId ? (
            <WSElement className={styles.addLineItem}>
              <WSButton.Link icon="paperclip" onClick={handleAddAttachment}>
                Add attachment
              </WSButton.Link>
            </WSElement>
          ) : (
            <WSText.ParagraphSm color="gray400">
              No attachments
            </WSText.ParagraphSm>
          )}
        </WSElement>
      </WSCard>
    </WSElement>
  );
};
